import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { Country } from '../../models/common/country.model';
import { City } from '../../models/common/city.model';
import { LocationService } from '../../location/location.service';
import { LazyLocationPickerDirective } from '../../shared/location-picker/lazy-location-picker.directive';
import { BaseMasterDataService } from '../../services/base-master-data.service';
import { SessionStorageService } from '../../services/session-storage.service';
import {
  LocationZone,
  LocationZoneCoverage,
  LocationZoneCoverageType
} from '../transport-planning.model';
import { TransportPlanningService } from '../transport-planning.service';

@Component({
  selector: 'app-location-zone-setup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    LazyLocationPickerDirective,
    TableModule,
    TagModule,
    TooltipModule
  ],
  templateUrl: './location-zone-setup.component.html',
  styleUrls: ['./location-zone-setup.component.css']
})
export class LocationZoneSetupComponent implements OnInit {
  zones: LocationZone[] = [];
  countries: Country[] = [];
  representativeLocationNames: Record<number, string> = {};
  cityOptions = new Map<number, City[]>();
  editorVisible = false;
  saving = false;
  loading = false;
  message = '';
  editor: LocationZone;

  readonly coverageTypes: Array<{ label: string; value: LocationZoneCoverageType }> = [
    { label: 'Entire country', value: 'COUNTRY' },
    { label: 'Entire city', value: 'CITY' },
    { label: 'District', value: 'DISTRICT' }
  ];

  constructor(
    private transportService: TransportPlanningService,
    private masterDataService: BaseMasterDataService,
    private locationService: LocationService,
    private session: SessionStorageService
  ) {
    this.editor = this.emptyZone();
  }

  ngOnInit(): void {
    const companyId = this.companyId();
    if (!companyId) return;
    this.loading = true;
    forkJoin({
      zones: this.transportService.getLocationZones(companyId),
      countries: this.masterDataService.getCountries('en-US')
        .pipe(catchError(() => of(this.session.getCountryList() ?? [])))
    }).pipe(finalize(() => this.loading = false)).subscribe({
      next: result => {
        this.zones = result.zones ?? [];
        this.countries = result.countries ?? [];
        this.loadRepresentativeLocationNames(this.zones);
      },
      error: error => this.message = this.errorMessage(error)
    });
  }

  openCreate(): void {
    this.message = '';
    this.editor = this.emptyZone();
    this.editor.coverages.push(this.emptyCoverage());
    this.editorVisible = true;
  }

  openEdit(zone: LocationZone): void {
    this.message = '';
    this.editor = this.copyZone(zone);
    this.loadCoverageCities(this.editor.coverages);
    this.editorVisible = true;
  }

  resetEditor(): void {
    this.message = '';
    const original = this.editor.id
      ? this.zones.find(zone => zone.id === this.editor.id)
      : null;
    this.editor = original ? this.copyZone(original) : this.emptyZone();
    if (!original) this.editor.coverages.push(this.emptyCoverage());
    this.loadCoverageCities(this.editor.coverages);
  }

  addCoverage(): void {
    this.editor.coverages.push(this.emptyCoverage());
  }

  removeCoverage(index: number): void {
    this.editor.coverages.splice(index, 1);
  }

  onCoverageTypeChanged(coverage: LocationZoneCoverage): void {
    if (coverage.coverageType === 'COUNTRY') {
      coverage.cityId = null;
      coverage.district = null;
    } else if (coverage.coverageType === 'CITY') {
      coverage.district = null;
    }
  }

  onCoverageCountryChanged(coverage: LocationZoneCoverage): void {
    coverage.cityId = null;
    coverage.district = null;
    if (coverage.countryId) {
      this.loadCities(coverage.countryId);
    }
  }

  citiesFor(coverage: LocationZoneCoverage): City[] {
    return coverage.countryId ? this.cityOptions.get(coverage.countryId) ?? [] : [];
  }

  save(): void {
    this.message = '';
    const validationMessage = this.validate();
    if (validationMessage) {
      this.message = validationMessage;
      return;
    }
    this.saving = true;
    const request = this.editor.id
      ? this.transportService.updateLocationZone(this.editor)
      : this.transportService.createLocationZone(this.editor);
    request.pipe(finalize(() => this.saving = false)).subscribe({
      next: saved => {
        const index = this.zones.findIndex(zone => zone.id === saved.id);
        if (index >= 0) this.zones[index] = saved;
        else this.zones = [...this.zones, saved];
        this.zones = [...this.zones].sort((a, b) => a.name.localeCompare(b.name));
        this.editorVisible = false;
      },
      error: error => this.message = this.errorMessage(error)
    });
  }

  toggleActive(zone: LocationZone): void {
    if (!zone.id) return;
    this.transportService.setLocationZoneActive(zone.id, this.companyId(), !zone.active).subscribe({
      next: saved => {
        const index = this.zones.findIndex(item => item.id === saved.id);
        if (index >= 0) this.zones[index] = saved;
        this.zones = [...this.zones];
      },
      error: error => this.message = this.errorMessage(error)
    });
  }

  coverageLabel(zone: LocationZone): string {
    const counts = zone.coverages.reduce((result, coverage) => {
      result[coverage.coverageType] = (result[coverage.coverageType] ?? 0) + 1;
      return result;
    }, {} as Record<string, number>);
    return [
      counts['COUNTRY'] ? `${counts['COUNTRY']} countries` : '',
      counts['CITY'] ? `${counts['CITY']} cities` : '',
      counts['DISTRICT'] ? `${counts['DISTRICT']} districts` : ''
    ].filter(Boolean).join(' / ') || 'No coverage';
  }

  neighbourOptions(): LocationZone[] {
    return this.zones.filter(zone => zone.active && zone.id !== this.editor.id);
  }

  countryName(countryId: number | null): string {
    return this.countries.find(country => country.id === countryId)?.name ?? `Country #${countryId}`;
  }

  cityName(coverage: LocationZoneCoverage): string {
    return this.citiesFor(coverage).find(city => city.id === coverage.cityId)?.cityName
      ?? (coverage.cityId ? `City #${coverage.cityId}` : '');
  }

  locationName(locationId: number | null | undefined): string {
    if (!locationId) return '-';
    return this.representativeLocationNames[locationId] ?? `Location #${locationId}`;
  }

  private loadRepresentativeLocationNames(zones: LocationZone[]): void {
    this.representativeLocationNames = {};
    const locationIds = [...new Set(zones
      .map(zone => zone.representativeLocationId)
      .filter((locationId): locationId is number => !!locationId))];
    for (const locationId of locationIds) {
      this.locationService.getLocationById(locationId).pipe(catchError(() => of({ data: [] } as any))).subscribe(response => {
        const name = response.data?.[0]?.name;
        if (name) {
          this.representativeLocationNames = { ...this.representativeLocationNames, [locationId]: name };
        }
      });
    }
  }

  coverageTitle(coverage: LocationZoneCoverage): string {
    const country = this.countryName(coverage.countryId);
    if (coverage.coverageType === 'COUNTRY') return country;
    const city = this.cityName(coverage);
    return coverage.coverageType === 'DISTRICT'
      ? `${country} / ${city} / ${coverage.district ?? ''}`
      : `${country} / ${city}`;
  }

  private loadCoverageCities(coverages: LocationZoneCoverage[]): void {
    new Set(coverages.map(coverage => coverage.countryId).filter(Boolean) as number[])
      .forEach(countryId => this.loadCities(countryId));
  }

  private loadCities(countryId: number): void {
    if (this.cityOptions.has(countryId)) return;
    this.cityOptions.set(countryId, []);
    this.masterDataService.getCities('en-US', countryId)
      .pipe(catchError(() => of([] as City[])))
      .subscribe(cities => this.cityOptions.set(countryId, cities ?? []));
  }

  private validate(): string {
    if (!this.editor.name.trim()) return 'Zone name is required.';
    if (!this.editor.code.trim()) return 'Zone code is required.';
    if (!this.editor.coverages.length) return 'Add at least one coverage row.';
    for (const coverage of this.editor.coverages) {
      if (!coverage.countryId) return 'Select a country for every coverage row.';
      if (coverage.coverageType !== 'COUNTRY' && !coverage.cityId) {
        return 'Select a city for city and district coverage.';
      }
      if (coverage.coverageType === 'DISTRICT' && !coverage.district?.trim()) {
        return 'Enter a district for district coverage.';
      }
    }
    return '';
  }

  private emptyZone(): LocationZone {
    return {
      owningCompanyId: this.companyId(),
      name: '',
      code: '',
      description: '',
      representativeLocationId: null,
      active: true,
      coverages: [],
      neighbourZoneIds: []
    };
  }

  private emptyCoverage(): LocationZoneCoverage {
    return { coverageType: 'COUNTRY', countryId: null, cityId: null, district: null };
  }

  private copyZone(zone: LocationZone): LocationZone {
    return {
      ...zone,
      coverages: zone.coverages.map(coverage => ({ ...coverage })),
      neighbourZoneIds: [...(zone.neighbourZoneIds ?? [])]
    };
  }

  private companyId(): number {
    return this.session.getCompany()?.id ?? 0;
  }

  private errorMessage(error: any): string {
    return error?.error?.message || error?.error?.detail || error?.message || 'Unable to save the location zone.';
  }
}
